import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { ArrowLeft, Plus, Mail, Phone, MapPin, Users, Calendar, DollarSign, Clock, ExternalLink } from 'lucide-react';
import { getQuoteById, updateQuoteStatus } from '@/services/quoteService';
import type { QuoteRequest, QuoteStatus } from '@/types';
import { formatCurrency, formatDate, formatPhone } from '@/lib/utils';

export default function QuoteRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [quote, setQuote] = useState<QuoteRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load quote
  useEffect(() => {
    if (!id || !user || role !== 'admin') {
      return;
    }

    const loadQuote = async () => {
      try {
        setLoading(true);
        const quoteData = await getQuoteById(id);

        if (!quoteData) {
          toast({
            title: 'Not Found',
            description: 'Quote request not found.',
            variant: 'destructive',
          });
          navigate('/admin/quote-requests');
          return;
        }

        setQuote(quoteData);
      } catch (error) {
        console.error('[QuoteRequestDetail] Error loading quote:', error);
        toast({
          title: 'Error',
          description: 'Failed to load quote request. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadQuote();
  }, [id, user, role, navigate, toast]);

  const handleStatusUpdate = async (newStatus: QuoteStatus) => {
    if (!quote) return;

    try {
      setUpdating(true);
      await updateQuoteStatus(quote.id, newStatus);

      setQuote({ ...quote, status: newStatus });

      toast({
        title: 'Status Updated',
        description: `Quote status changed to ${newStatus}.`,
      });
    } catch (error) {
      console.error('[QuoteRequestDetail] Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateOffer = () => {
    if (!quote) return;
    navigate(`/admin/route-offers/create?quoteId=${quote.id}`);
  };

  const getStatusColor = (status: QuoteStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'quoted':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: QuoteStatus) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'hubstop': 'Hubstop',
      'yearly': 'Yearly',
      'summer-camp': 'Summer Camp',
      'custom': 'Custom',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading quote details...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/admin/quote-requests')}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Quote Requests
        </Button>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">Quote Request</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(quote.status)}`}>
                {getStatusLabel(quote.status)}
              </span>
            </div>
            <p className="text-gray-600">
              {getTypeLabel(quote.type)} • Submitted {formatDate(quote.createdAt)}
            </p>
          </div>

          {quote.offerId ? (
            <Link to={`/admin/route-offers/${quote.offerId}`}>
              <Button variant="outline" className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                View Linked Offer
              </Button>
            </Link>
          ) : (
            <Button
              onClick={handleCreateOffer}
              className="flex items-center gap-2"
              disabled={quote.status === 'accepted'}
            >
              <Plus className="w-4 h-4" />
              Create Offer from Quote
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guardian Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Guardian Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Name</label>
                  <p className="text-gray-900 font-medium">{quote.guardianName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <a href={`mailto:${quote.guardianEmail}`} className="text-primary hover:underline">
                    {quote.guardianEmail}
                  </a>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    Phone
                  </label>
                  <a href={`tel:${quote.guardianPhone}`} className="text-primary hover:underline">
                    {formatPhone(quote.guardianPhone)}
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Route Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Route Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-green-500" />
                    Pickup Address
                  </label>
                  <p className="text-gray-900">{quote.pickupAddress}</p>
                  <p className="text-sm text-gray-500">Zipcode: {quote.pickupZipcode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-red-500" />
                    Dropoff Address
                  </label>
                  <p className="text-gray-900">{quote.dropoffAddress}</p>
                  <p className="text-sm text-gray-500">Zipcode: {quote.dropoffZipcode}</p>
                </div>
                {quote.hubstopName && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Hubstop</label>
                    <p className="text-gray-900">{quote.hubstopName}</p>
                  </div>
                )}
                {quote.routeName && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Route Name</label>
                    <p className="text-gray-900">{quote.routeName}</p>
                  </div>
                )}
                {quote.routeDistance && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Distance</label>
                    <p className="text-gray-900">{(quote.routeDistance * 0.000621371).toFixed(1)} miles</p>
                  </div>
                )}
                {quote.routeDuration && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Duration</label>
                    <p className="text-gray-900">{Math.ceil(quote.routeDuration / 60)} minutes</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Students */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Students ({quote.students.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {quote.students.map((student, index) => (
                    <div key={student.id || index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">
                        {student.firstName} {student.lastName}
                      </div>
                      {student.grade && (
                        <div className="text-sm text-gray-600">Grade: {student.grade}</div>
                      )}
                      {student.school && (
                        <div className="text-sm text-gray-600">School: {student.school}</div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            {quote.calculatedPrice && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {quote.priceBreakdown && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Base Price</span>
                          <span className="text-gray-900">{formatCurrency(quote.priceBreakdown.basePrice)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Students</span>
                          <span className="text-gray-900">{quote.priceBreakdown.numberOfStudents}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Sessions</span>
                          <span className="text-gray-900">{quote.priceBreakdown.numberOfSessions}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span className="text-gray-900">{formatCurrency(quote.priceBreakdown.subtotal)}</span>
                        </div>
                        {quote.priceBreakdown.discount && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Discount</span>
                            <span>-{formatCurrency(quote.priceBreakdown.discount)}</span>
                          </div>
                        )}
                        {quote.priceBreakdown.lateShuttleFee && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Late Shuttle Fee</span>
                            <span className="text-gray-900">{formatCurrency(quote.priceBreakdown.lateShuttleFee)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                          <span>Total</span>
                          <span>{formatCurrency(quote.calculatedPrice)}</span>
                        </div>
                      </>
                    )}
                    {!quote.priceBreakdown && (
                      <div className="text-2xl font-bold text-gray-900">
                        {formatCurrency(quote.calculatedPrice)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-gray-400 mt-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Created</div>
                      <div className="text-sm text-gray-500">{formatDate(quote.createdAt)}</div>
                    </div>
                  </div>
                  {quote.quotedAt && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Quoted</div>
                        <div className="text-sm text-gray-500">{formatDate(quote.quotedAt)}</div>
                      </div>
                    </div>
                  )}
                  {quote.expiresAt && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-orange-500 mt-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(quote.expiresAt) > new Date() ? 'Expires' : 'Expired'}
                        </div>
                        <div className="text-sm text-gray-500">{formatDate(quote.expiresAt)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Linked Offer */}
            {quote.offerId && (
              <Card>
                <CardHeader>
                  <CardTitle>Linked Offer</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    This quote has a route offer created from it.
                  </p>
                  <Link to={`/admin/route-offers/${quote.offerId}`}>
                    <Button variant="outline" className="w-full flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      View Route Offer
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {quote.status === 'pending' && !quote.offerId && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleStatusUpdate('quoted')}
                    disabled={updating}
                  >
                    Mark as Quoted
                  </Button>
                )}
                {(quote.status === 'pending' || quote.status === 'quoted') && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleStatusUpdate('expired')}
                    disabled={updating}
                  >
                    Mark as Expired
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open(`mailto:${quote.guardianEmail}`, '_blank')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Quote Type</label>
                  <p className="text-gray-900">{getTypeLabel(quote.type)}</p>
                </div>
                {quote.selectedSessions && quote.selectedSessions.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Selected Weeks</label>
                    <p className="text-gray-900">{quote.selectedSessions.length} weeks</p>
                  </div>
                )}
                {quote.semesterType && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Semester</label>
                    <p className="text-gray-900 capitalize">{quote.semesterType}</p>
                  </div>
                )}
                {quote.lateShuttle && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Late Shuttle</label>
                    <p className="text-gray-900">Yes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
